import React from 'react';
import { Card } from '@/components/ui/card';
import { Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <Card className="p-8 glass-card">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
        </div>

        <div className="space-y-6 text-muted-foreground">
          <p className="text-sm">Last Updated: March 24, 2026</p>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Introduction</h2>
            <p>
              PeptideForge ("we", "our", "us") respects your privacy and is committed to protecting your personal data. 
              This privacy policy explains how we collect, use, and safeguard your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Information We Collect</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Account Information:</strong> Email address, name, password (encrypted)</li>
              <li><strong>Usage Data:</strong> Dose logs, inventory records, calculations, schedules</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store card details)</li>
              <li><strong>Analytics:</strong> App usage patterns, feature interactions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide and maintain our services</li>
              <li>Process payments and subscriptions</li>
              <li>Send important updates and notifications</li>
              <li>Improve app features and user experience</li>
              <li>Respond to support requests</li>
              <li>Ensure security and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Encryption in transit (HTTPS/TLS)</li>
              <li>Encrypted data storage</li>
              <li>Secure authentication (JWT tokens, OAuth)</li>
              <li>Regular security audits</li>
              <li>Limited employee access to data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Data Sharing</h2>
            <p className="mb-2">
              We do NOT sell your personal data. We may share data only with:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Payment Processors:</strong> Stripe for subscription management</li>
              <li><strong>Cloud Services:</strong> Hosting and database providers</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Your Rights (GDPR)</h2>
            <p className="mb-2">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request data deletion</li>
              <li>Export your data (CSV)</li>
              <li>Withdraw consent</li>
              <li>Object to processing</li>
            </ul>
            <p className="mt-3">
              Contact us at privacy@peptideforge.com to exercise your rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Cookies</h2>
            <p>
              We use essential cookies for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Authentication (session tokens)</li>
              <li>User preferences (theme, settings)</li>
              <li>Analytics (anonymous usage data)</li>
            </ul>
            <p className="mt-3">
              You can disable cookies in your browser, but some features may not work properly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Data Retention</h2>
            <p>
              We retain your data for as long as your account is active. After account deletion, we may retain anonymized data for 
              analytics and legal compliance purposes for up to 90 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Children's Privacy</h2>
            <p>
              PeptideForge is not intended for users under 18. We do not knowingly collect data from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Changes to This Policy</h2>
            <p>
              We may update this policy periodically. Significant changes will be notified via email or in-app notification.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">Contact Us</h2>
            <p>
              For privacy-related questions or requests:
            </p>
            <ul className="list-none space-y-1 ml-4 mt-2">
              <li>Email: privacy@peptideforge.com</li>
              <li>Support: support@peptideforge.com</li>
            </ul>
          </section>
        </div>
      </Card>
    </div>
  );
}